import { useEffect, useRef, useState } from "react";
import { X, ChevronUp, ChevronDown, Loader2, MoveVertical } from "lucide-react";

/* Reel Mode — Instagram/TikTok-style vertical-scroll try-on.
   The customer swipes up/down (or scroll-wheels on a kiosk) and instantly
   sees themselves in the next saree. A thumbnail strip at the bottom lets
   them jump to any saree directly.

   Props:
   - sarees:        array of {id, title, ai_model_images, price}
   - initialIndex:  number — which saree to start on
   - tryOn(id):     async function — runs the AI try-on, fills the cache
   - cache:         ref { [id::bg]: dataUrl } — same cache the parent uses
   - background:    current background preset key (for cache lookup)
   - generating:    boolean — parent's loading flag (for "still generating" state)
   - tryOnImg:      latest try-on image (matches activeSareeId)
   - activeSareeId: currently rendered saree
   - onExit:        () => void — closes Reel mode and returns to normal Mirror
*/
export default function MirrorReelMode({
    sarees, initialIndex = 0, tryOn, cache, background,
    generating, tryOnImg, activeSareeId, onExit,
}) {
    const [index, setIndex] = useState(initialIndex);
    const wheelLockRef = useRef(false);
    const touchStartYRef = useRef(null);
    const stripRef = useRef(null);

    const current = sarees[index];

    // When index changes, fire a try-on for the new saree (if not cached).
    useEffect(() => {
        if (!current) return;
        tryOn(current.id);
        // Scroll the thumbnail strip so the active thumb is centred.
        const el = stripRef.current?.querySelector(`[data-thumb-id="${current.id}"]`);
        if (el && stripRef.current) {
            stripRef.current.scrollTo({
                left: el.offsetLeft - stripRef.current.clientWidth / 2 + el.clientWidth / 2,
                behavior: "smooth",
            });
        }
        // Prefetch the NEXT 2 sarees in the background — feels instant on swipe.
        for (let lookahead = 1; lookahead <= 2; lookahead++) {
            const next = sarees[index + lookahead];
            if (next) {
                const ck = `${next.id}::${background}`;
                if (cache && !cache.current?.[ck]) {
                    setTimeout(() => tryOn(next.id, undefined, /*silent*/ true), 100 + lookahead * 700);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index]);

    const goNext = () => setIndex((i) => Math.min(sarees.length - 1, i + 1));
    const goPrev = () => setIndex((i) => Math.max(0, i - 1));

    // Touch swipe (mobile / kiosk touchscreen)
    const onTouchStart = (e) => { touchStartYRef.current = e.touches[0].clientY; };
    const onTouchEnd = (e) => {
        const startY = touchStartYRef.current;
        if (startY == null) return;
        const endY = e.changedTouches[0].clientY;
        const dy = startY - endY;
        if (Math.abs(dy) < 40) return; // ignore taps
        if (dy > 0) goNext(); else goPrev();
        touchStartYRef.current = null;
    };

    // Mouse wheel (laptop) — debounced so one flick = one card
    const onWheel = (e) => {
        if (wheelLockRef.current) return;
        wheelLockRef.current = true;
        setTimeout(() => { wheelLockRef.current = false; }, 350);
        if (e.deltaY > 0) goNext(); else goPrev();
    };

    // Keyboard (kiosk with bluetooth remote / staff override)
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowDown" || e.key === " ") { e.preventDefault(); goNext(); }
            else if (e.key === "ArrowUp") { e.preventDefault(); goPrev(); }
            else if (e.key === "Escape") onExit?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [sarees.length]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!current) return null;

    // Show the cached/generated image for the currently-displayed saree.
    const displayImg = activeSareeId === current.id ? tryOnImg : null;
    const isLoading = generating && activeSareeId === current.id && !displayImg;
    // Instant placeholder: a saree photo loads in <1 sec so the customer NEVER
    // sees a blank screen while Gemini works. Prefer the AI catalog photo if
    // present, otherwise fall back to the raw product photo.
    const placeholderImg = current.ai_model_images?.[0] || current.images?.[0];

    return (
        <div
            className="fixed inset-0 z-50 bg-black text-white select-none"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onWheel={onWheel}
            data-testid="mirror-reel-mode"
        >
            {/* Try-on image area — placeholder + fade-in personalized try-on */}
            <div className="absolute inset-0 flex items-center justify-center">
                {/* Layer 1: always-on placeholder (saree catalog photo) — appears instantly */}
                {placeholderImg ? (
                    <img
                        src={placeholderImg}
                        alt={current.title}
                        className={`absolute max-h-full max-w-full object-contain transition-opacity duration-500 ${
                            displayImg ? "opacity-0" : "opacity-60"
                        }`}
                        loading="eager"
                        decoding="async"
                    />
                ) : null}
                {/* Layer 2: real personalized try-on — fades in when ready */}
                {displayImg ? (
                    <img
                        src={displayImg}
                        alt={current.title}
                        className="max-h-full max-w-full object-contain animate-in fade-in duration-500"
                        data-testid="reel-active-img"
                    />
                ) : isLoading ? (
                    <div className="absolute bottom-1/3 text-center">
                        <Loader2 className="w-10 h-10 animate-spin mx-auto opacity-90" strokeWidth={1.2} />
                        <div className="mt-2 text-xs opacity-80 bg-black/40 px-3 py-1 rounded-full inline-block backdrop-blur">
                            Personalising on you…
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Top bar — exit + counter + swipe hint */}
            <div className="absolute top-0 left-0 right-0 p-3 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
                <button
                    onClick={onExit}
                    className="bg-white/20 hover:bg-white/30 rounded-full p-2 backdrop-blur"
                    data-testid="reel-exit-btn"
                    aria-label="Exit reel mode"
                >
                    <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
                <div className="text-xs font-medium bg-white/20 px-3 py-1 rounded-full backdrop-blur">
                    {index + 1} / {sarees.length}
                </div>
                <div className="w-9 h-9" />{/* spacer */}
            </div>

            {/* Saree title + price overlay */}
            <div className="absolute left-3 right-20 bottom-32 max-w-sm pointer-events-none">
                <div className="font-serif text-2xl drop-shadow-lg" data-testid="reel-active-title">
                    {current.title}
                </div>
                {current.price ? (
                    <div className="text-base mt-1 opacity-90">₹{Number(current.price).toLocaleString("en-IN")}</div>
                ) : null}
            </div>

            {/* Right-side swipe buttons (also help on laptops without scroll) */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col gap-3">
                <button
                    onClick={goPrev}
                    disabled={index === 0}
                    className="bg-white/20 hover:bg-white/30 disabled:opacity-30 rounded-full p-3 backdrop-blur"
                    data-testid="reel-prev-btn"
                    aria-label="Previous saree"
                >
                    <ChevronUp className="w-6 h-6" strokeWidth={1.5} />
                </button>
                <button
                    onClick={goNext}
                    disabled={index >= sarees.length - 1}
                    className="bg-white/20 hover:bg-white/30 disabled:opacity-30 rounded-full p-3 backdrop-blur"
                    data-testid="reel-next-btn"
                    aria-label="Next saree"
                >
                    <ChevronDown className="w-6 h-6" strokeWidth={1.5} />
                </button>
            </div>

            {/* Swipe hint — only on first card */}
            {index === 0 ? (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-44 flex items-center gap-1.5 text-xs opacity-80 animate-pulse pointer-events-none">
                    <MoveVertical className="w-3.5 h-3.5" /> Swipe to try more sarees
                </div>
            ) : null}

            {/* Bottom thumbnail strip */}
            <div className="absolute left-0 right-0 bottom-0 p-3 bg-gradient-to-t from-black/80 via-black/60 to-transparent">
                <div
                    ref={stripRef}
                    className="flex gap-2 overflow-x-auto pb-1 scroll-smooth scrollbar-none"
                    style={{ scrollbarWidth: "none" }}
                    data-testid="reel-thumb-strip"
                >
                    {sarees.map((s, i) => (
                        <button
                            key={s.id}
                            data-thumb-id={s.id}
                            onClick={() => setIndex(i)}
                            className={`relative shrink-0 w-16 aspect-[3/4] rounded-md overflow-hidden border-2 transition ${
                                i === index ? "border-white scale-110" : "border-white/30 opacity-70"
                            }`}
                            data-testid={`reel-thumb-${s.id}`}
                        >
                            {(s.ai_model_images?.[0] || s.images?.[0]) ? (
                                <img
                                    src={s.ai_model_images?.[0] || s.images?.[0]}
                                    alt={s.title}
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover"
                                />
                            ) : null}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-[8px] py-0.5 px-1 truncate">
                                #{String(i + 1).padStart(3, "0")}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
